import { useMemo } from 'react'

import Dagre from '@dagrejs/dagre'
import { Edge } from '@xyflow/react'

import { NODE_TYPES, normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'

import { CustomNode, SIZES } from './types'

export type UseExpandCollapseOptions = {
  layoutNodes?: boolean
  treeWidth?: number
  treeHeight?: number
}

function filterCollapsedChildren(dagre: Dagre.graphlib.Graph, node: CustomNode) {
  const children = dagre.successors(node.id) ?? undefined

  // Update this node's props so it knows if it has children and can be expanded
  // or not.
  node.data.expandable = !!children?.length

  // If the node is collpased (ie it is not expanded) then we want to remove all
  // of its children from the graph *and* any of their children.
  if (!node.data.expanded) {
    while (children?.length) {
      const child = children.pop()

      if (child) {
        const childSuccessors = dagre.successors(child)
        if (childSuccessors) {
          children.push(...childSuccessors)
        }

        dagre.removeNode(child)
      }
    }
  }
}

function buildChildrenMap(edges: Edge[]): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>()
  edges.forEach((edge) => {
    const children = childrenByParent.get(edge.source) || []
    children.push(edge.target)
    childrenByParent.set(edge.source, children)
  })
  return childrenByParent
}

function buildParentMap(edges: Edge[]): Map<string, string> {
  const parentByChild = new Map<string, string>()
  edges.forEach((edge) => {
    parentByChild.set(edge.target, edge.source)
  })
  return parentByChild
}

function buildNodeMap(nodes: CustomNode[]): Map<string, CustomNode> {
  const nodeMap = new Map<string, CustomNode>()
  nodes.forEach((node) => {
    nodeMap.set(node.id, node)
  })
  return nodeMap
}

function getLevelChildren(
  parentId: string,
  nodePositions: Map<string, { x: number; y: number }>,
  childrenByParent: Map<string, string[]>,
  nodeMap: Map<string, CustomNode>
): string[] {
  return (
    childrenByParent.get(parentId)?.filter((childId) => {
      const childNode = nodeMap.get(childId)
      return (
        childNode &&
        normalizeNodeType(childNode.data.type) === NODE_TYPES.LEVEL &&
        nodePositions.has(childId)
      )
    }) || []
  )
}

function repositionLevelNodes(
  nodes: CustomNode[],
  nodePositions: Map<string, { x: number; y: number }>,
  childrenByParent: Map<string, string[]>,
  nodeMap: Map<string, CustomNode>
) {
  nodes.forEach((node) => {
    if (!node.data.hasLevelChildren || !node.data.expanded) {
      return
    }

    const parentPos = nodePositions.get(node.id)
    if (!parentPos) {
      return
    }

    const levelChildren = getLevelChildren(node.id, nodePositions, childrenByParent, nodeMap)
    if (levelChildren.length === 0) {
      return
    }

    // Position level children vertically, stacked on top of each other
    let currentY = parentPos.y + SIZES.INITIAL_LEVEL_NODE_VERTICAL_SPACING
    levelChildren.forEach((childId) => {
      nodePositions.set(childId, {
        x: parentPos.x,
        y: currentY,
      })
      currentY += SIZES.NODE_LEVEL_HEIGHT + SIZES.VERTICAL_SPACING_LEVEL_NODE
    })
  })
}

function buildSiblingsMap(
  nodes: CustomNode[],
  parentByChild: Map<string, string>
): Map<string, string[]> {
  const siblingsByParent = new Map<string, string[]>()
  nodes.forEach((node) => {
    const parentId = parentByChild.get(node.id)
    if (parentId) {
      const siblings = siblingsByParent.get(parentId) || []
      siblings.push(node.id)
      siblingsByParent.set(parentId, siblings)
    }
  })
  return siblingsByParent
}

function shouldCompactSiblings(siblingIds: string[], nodeMap: Map<string, CustomNode>): boolean {
  if (siblingIds.length <= 1) {
    return false
  }

  return siblingIds.some((siblingId) => {
    const sibling = nodeMap.get(siblingId)
    return sibling?.data.hasLevelChildren && sibling.data.expanded
  })
}

function getRoleSiblings(
  siblingIds: string[],
  nodePositions: Map<string, { x: number; y: number }>,
  nodeMap: Map<string, CustomNode>
): Array<{ id: string; node: CustomNode; pos: { x: number; y: number } }> {
  return siblingIds
    .map((id) => {
      const node = nodeMap.get(id)
      const pos = nodePositions.get(id)
      if (!node || !pos) return null
      const isLevel = normalizeNodeType(node.data.type) === NODE_TYPES.LEVEL
      return isLevel ? null : { id, node, pos }
    })
    .filter(
      (item): item is { id: string; node: CustomNode; pos: { x: number; y: number } } =>
        item !== null
    )
    .sort((a, b) => a.pos.x - b.pos.x)
}

function moveLevelChildrenWithParent(
  parentId: string,
  newParentX: number,
  oldParentX: number,
  nodePositions: Map<string, { x: number; y: number }>,
  childrenByParent: Map<string, string[]>,
  nodeMap: Map<string, CustomNode>
) {
  const levelChildren = getLevelChildren(parentId, nodePositions, childrenByParent, nodeMap)
  const deltaX = newParentX - oldParentX

  levelChildren.forEach((childId) => {
    const childPos = nodePositions.get(childId)
    if (childPos) {
      nodePositions.set(childId, {
        x: childPos.x + deltaX,
        y: childPos.y,
      })
    }
  })
}

function compactSiblingsWithLevelChildren(
  nodes: CustomNode[],
  nodePositions: Map<string, { x: number; y: number }>,
  childrenByParent: Map<string, string[]>,
  parentByChild: Map<string, string>,
  nodeMap: Map<string, CustomNode>
) {
  const siblingsByParent = buildSiblingsMap(nodes, parentByChild)

  siblingsByParent.forEach((siblingIds, parentId) => {
    if (!shouldCompactSiblings(siblingIds, nodeMap)) {
      return
    }

    const parentPos = nodePositions.get(parentId)
    if (!parentPos) {
      return
    }

    const roleSiblings = getRoleSiblings(siblingIds, nodePositions, nodeMap)
    if (roleSiblings.length === 0) {
      return
    }

    // Compact siblings horizontally around the parent's X position
    const spacing = SIZES.TREE_WIDTH_SPACING
    const totalWidth = (roleSiblings.length - 1) * spacing
    const startX = parentPos.x - totalWidth / 2

    roleSiblings.forEach((sibling, index) => {
      const newX = startX + index * spacing
      const oldX = sibling.pos.x

      // Move the role node
      nodePositions.set(sibling.id, {
        x: newX,
        y: sibling.pos.y,
      })

      // Move all level children of this role node to maintain alignment with parent
      if (sibling.node.data.hasLevelChildren && sibling.node.data.expanded) {
        moveLevelChildrenWithParent(
          sibling.id,
          newX,
          oldX,
          nodePositions,
          childrenByParent,
          nodeMap
        )
      }
    })
  })
}

function useExpandCollapse(
  nodes: CustomNode[],
  edges: Edge[],
  {
    layoutNodes = true,
    treeWidth = SIZES.TREE_WIDTH_SPACING,
    treeHeight = SIZES.TREE_HEIGHT_SPACING,
  }: UseExpandCollapseOptions = {}
): { nodes: CustomNode[]; edges: Edge[] } {
  return useMemo(() => {
    if (!layoutNodes) return { nodes, edges }

    // 1. Create a new instance of `Dagre.graphlib.Graph` and set some default
    // properties.
    const dagre = new Dagre.graphlib.Graph()
      .setDefaultEdgeLabel(() => ({}))
      .setGraph({ rankdir: 'TB' })

    // 2. Add each node and edge to the dagre graph. Use minimal width for level nodes
    // since they will be stacked vertically and don't need horizontal space.
    for (const node of nodes) {
      const isLevelNode = normalizeNodeType(node.data.type) === NODE_TYPES.LEVEL

      dagre.setNode(node.id, {
        // Level nodes use minimal width since they stack vertically, not horizontally
        width: isLevelNode ? SIZES.NODE_WIDTH : treeWidth,
        height: isLevelNode ? SIZES.NODE_LEVEL_HEIGHT : treeHeight,
        data: node.data,
      })
    }

    for (const edge of edges) {
      dagre.setEdge(edge.source, edge.target)
    }

    // 3. Iterate over the nodes *again* to determine which ones should be hidden
    // based on expand/collapse state. Hidden nodes are removed from the dagre
    // graph entirely.
    for (const node of nodes) {
      filterCollapsedChildren(dagre, node)
    }

    // 4. Run the dagre layouting algorithm.
    Dagre.layout(dagre)

    // 5. Build helper maps for efficient lookups
    const nodePositions = new Map<string, { x: number; y: number }>()
    nodes.forEach((node) => {
      if (dagre.hasNode(node.id)) {
        const { x, y } = dagre.node(node.id)
        nodePositions.set(node.id, { x, y })
      }
    })

    const childrenByParent = buildChildrenMap(edges)
    const parentByChild = buildParentMap(edges)
    const nodeMap = buildNodeMap(nodes)

    // 6. Re-position level children vertically for nodes with hasLevelChildren
    repositionLevelNodes(nodes, nodePositions, childrenByParent, nodeMap)

    // 7. Compact horizontal spacing for siblings when some have level children
    // Since level nodes stack vertically, siblings should be closer together
    compactSiblingsWithLevelChildren(nodes, nodePositions, childrenByParent, parentByChild, nodeMap)

    return {
      // 8. Return a new array of layouted nodes. This will not include any nodes
      // that were removed from the dagre graph in step 3.
      //
      // 💡 `Array.flatMap` can act as a *filter map*. If we want to remove an
      // element from the array, we can return an empty array in this iteration.
      // Otherwise, we can map the element like normal and wrap it in a singleton
      // array.
      nodes: nodes.flatMap((node) => {
        // This node might have been filtered out by `filterCollapsedChildren` if
        // any of its ancestors were collpased.
        const position = nodePositions.get(node.id)
        if (!position) return []

        // 🚨 `filterCollapsedChildren` *mutates* the data object of a node. React
        // will not know the data has changed unless we create a new object here.
        const data = { ...node.data }

        return [{ ...node, position, data }]
      }),
      edges,
    }
  }, [nodes, edges, layoutNodes, treeWidth, treeHeight])
}

export default useExpandCollapse
